/* Pre-sale product page (product.presale template): gallery, zoom, fabric meter, make it a set, shop the drop,
   mobile pre-order bar. One request per print feeds the meter, the add-on and the drop grid:
   /collections/all/<print-tag>?section_id=presale-data (sections/presale-data.liquid). */
(() => {
  if (window.__presalePdp) return;
  window.__presalePdp = true;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const prints = {};

  /* ---------- Shared ---------- */

  function loadPrint(handle) {
    if (!prints[handle]) {
      const url = `${root}collections/all/${encodeURIComponent(handle)}?section_id=presale-data&sort_by=best-selling`;
      prints[handle] = fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`presale-data ${response.status}`))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const meta = doc.querySelector('[data-pp-meta]');
          const json = doc.querySelector('[data-pp-products]');
          const fill = meta && meta.dataset.fill !== undefined ? parseInt(meta.dataset.fill, 10) : null;
          return {
            fill: Number.isNaN(fill) ? null : fill,
            products: json ? JSON.parse(json.textContent) : []
          };
        });
    }
    return prints[handle];
  }

  const fill = (template, values) =>
    (template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  function formatMoney(cents) {
    const format = (window.themeVariables && themeVariables.settings && themeVariables.settings.moneyFormat) || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\w*\s*\}\}/, amount);
  }

  function shortTitle(title, printName) {
    let rest = title;
    if (printName && rest.toLowerCase().startsWith(printName.toLowerCase())) rest = rest.slice(printName.length).trim();
    rest = rest.replace(/^(Luxe|Dynamic)\s+/i, '');
    return rest || title;
  }

  // Globo's note for our own adds: assets/globo-presale-note.js (site-wide) only gives it for variants in an
  // active Globo profile that is inside its dates.
  function preorderProps(variantId) {
    return window.EmpressPresaleNote ? window.EmpressPresaleNote.propsFor(variantId) : undefined;
  }

  // Same flow as Prestige's product form: bundle the cart drawer section so it re-renders with the new item.
  async function addToCart(variantId, productId, { openDrawer = true } = {}) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    const item = { id: variantId, quantity: 1 };
    const props = preorderProps(variantId);
    if (props) item.properties = props;

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [item], sections: sections.join(','), sections_url: `${root}variants/${variantId}` })
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || 'Could not add');

    const cart = await (await fetch(`${root}cart.js`)).json();
    cart.sections = json.sections;
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: openDrawer ? 'variant:add' : 'presale:add', cart }
      })
    );
    return cart;
  }

  // Slide/fade in by dropping an "entering" class on the next tick. setTimeout rather than requestAnimationFrame,
  // which in-app browsers starve: worst case the element simply appears in place.
  function enter(el) {
    el.classList.add('is-entering');
    setTimeout(() => el.classList.remove('is-entering'), 30);
    // If frames are starved the transition never finishes; cancelling it snaps to the end state
    setTimeout(() => {
      el.style.transition = 'none';
      void el.offsetWidth;
      el.style.transition = '';
    }, 600);
  }

  // Smooth scroll with a safety net: if it hasn't moved at all (starved frames), jump there instead
  function smoothScroll(scroller, options) {
    const isWindow = scroller === window;
    const start = isWindow ? window.scrollY : scroller.scrollLeft;
    scroller.scrollTo({ ...options, behavior: reduceMotion ? 'auto' : 'smooth' });
    setTimeout(() => {
      const now = isWindow ? window.scrollY : scroller.scrollLeft;
      if (now === start) scroller.scrollTo({ ...options, behavior: 'auto' });
    }, 600);
  }

  function scrollToEl(el) {
    const offset = (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height'), 10) || 80) + 12;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    smoothScroll(window, { top });
  }

  /* ---------- Gallery + zoom ---------- */

  function initGallery(gallery) {
    const track = gallery.querySelector('[data-pp-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-pp-slide]'));
    const thumbStrip = gallery.querySelector('[data-pp-thumbs]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-pp-thumb]'));
    const count = gallery.querySelector('[data-pp-count]');
    if (!track || !slides.length) return;

    const setActive = (index) => {
      if (count) count.textContent = String(index + 1);
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      // Keep the active thumbnail in view without moving the page
      const thumb = thumbs[index];
      if (thumbStrip && thumb) {
        const target = thumb.offsetLeft - (thumbStrip.clientWidth - thumb.offsetWidth) / 2;
        thumbStrip.scrollTo({ left: Math.max(target, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        smoothScroll(track, { left: track.clientWidth * index });
        setActive(index);
      });
    });

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      setTimeout(() => {
        ticking = false;
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        setActive(Math.min(Math.max(index, 0), slides.length - 1));
      }, 80);
    }, { passive: true });

    const dialog = gallery.closest('[data-pp-main]').querySelector('[data-pp-zoom]');
    const zoomTrack = dialog && dialog.querySelector('[data-pp-zoom-track]');
    if (!dialog || !zoomTrack || typeof dialog.showModal !== 'function') return;

    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (!zoomTrack.children.length) {
          slides.forEach((s) => {
            const img = document.createElement('img');
            img.src = s.dataset.zoom;
            img.alt = '';
            img.loading = 'lazy';
            zoomTrack.appendChild(img);
          });
        }
        dialog.showModal();
        zoomTrack.scrollLeft = zoomTrack.clientWidth * index;
      });
    });

    dialog.querySelector('[data-pp-zoom-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  /* ---------- Fabric meter ---------- */

  // Just a bar: the feed sends a fill rounded to 5% (never the count or the cap) and only when the bar is
  // switched on in Theme settings > Pre-sale fabric bar. Below the "only show from" level it stays hidden.
  function initMeter(meter) {
    const track = meter.querySelector('[data-pp-meter-track]');
    if (!track || !meter.dataset.print) return;
    const from = parseInt(meter.dataset.barFrom, 10) || 0;

    loadPrint(meter.dataset.print)
      .then(({ fill: pct }) => {
        if (pct === null || pct < from) return;
        const fillEl = meter.querySelector('[data-pp-meter-fill]');
        track.hidden = false;
        fillEl.getBoundingClientRect(); // start the transition from 0
        fillEl.style.width = `${Math.min(Math.max(pct, 4), 100)}%`;
        // Snap to the end if frames are starved and the transition never runs
        setTimeout(() => {
          fillEl.style.transition = 'none';
          void fillEl.offsetWidth;
          fillEl.style.transition = '';
        }, 1600);
      })
      .catch(() => {});
  }

  /* ---------- Make it a set ---------- */

  function initAddon(addon) {
    const keyword = (addon.dataset.keyword || '').trim();
    if (!keyword || !addon.dataset.print) return;

    loadPrint(addon.dataset.print)
      .then(({ products }) => {
        const match = products.find(
          (p) => String(p.id) !== addon.dataset.productId && p.available && p.title.toLowerCase().includes(keyword)
        );
        if (!match) return;

        const img = addon.querySelector('[data-pp-addon-img]');
        img.style.aspectRatio = String(match.ratio || 1);
        if (match.image) {
          img.src = match.image;
          img.alt = match.title;
        } else {
          img.hidden = true;
        }
        addon.querySelector('[data-pp-addon-title]').textContent = match.title;
        addon.querySelector('[data-pp-addon-price]').textContent = match.price;
        addon.querySelector('[data-pp-addon-link]').href = match.url;

        const button = addon.querySelector('[data-pp-addon-btn]');
        const buttonText = addon.querySelector('[data-pp-addon-btn-text]');
        const error = addon.querySelector('[data-pp-addon-error]');
        const variant = match.variants.length === 1 && match.variants[0].available ? match.variants[0] : null;

        if (!variant) {
          button.addEventListener('click', () => { window.location.href = match.url; });
        } else {
          button.setAttribute('aria-label', `${buttonText.textContent.trim()} ${match.title}`);
          button.addEventListener('click', async () => {
            if (button.getAttribute('aria-busy') === 'true') return;
            button.setAttribute('aria-busy', 'true');
            error.hidden = true;
            try {
              await addToCart(variant.id, match.id);
              button.classList.add('is-added');
              buttonText.textContent = addon.dataset.addedText || 'Added';
            } catch (e) {
              error.textContent = e.message;
              error.hidden = false;
            } finally {
              button.removeAttribute('aria-busy');
            }
          });
        }

        addon.hidden = false;
      })
      .catch(() => {});
  }

  /* ---------- Shop the drop ---------- */

  function parseFilters(spec) {
    return (spec || '')
      .split('|')
      .map((part) => {
        const [name, words] = part.split(':');
        return name && words ? { name: name.trim(), words: words.split(',').map((w) => w.trim().toLowerCase()).filter(Boolean) } : null;
      })
      .filter(Boolean);
  }

  // Set by initDrop: its size sheet and bag toast, reused by Complete the look in the buy box
  let dropUi = null;

  const PLUS_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  const TICK_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5.5 12.5 4 4 9-9" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function initDrop(section) {
    const track = section.querySelector('[data-pp-grid]');
    const rail = section.querySelector('[data-pp-rail]');
    const filtersEl = section.querySelector('[data-pp-filters]');
    const progress = section.querySelector('[data-pp-progress]');
    const prev = section.querySelector('[data-pp-rail-prev]');
    const next = section.querySelector('[data-pp-rail-next]');
    const sheet = section.querySelector('[data-pp-sheet]');
    const toast = section.querySelector('[data-pp-toast]');
    const ds = section.dataset;
    if (!track || !rail || !ds.print) return;

    /* Rail: progress line + desktop arrows */
    const updateRail = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      const visible = max > 0 ? rail.clientWidth / rail.scrollWidth : 1;
      const ratio = max > 0 ? rail.scrollLeft / max : 0;
      if (progress) {
        const width = Math.max(visible, 0.12);
        progress.style.width = `${width * 100}%`;
        progress.style.transform = `translateX(${ratio * ((1 - width) / width) * 100}%)`;
      }
      if (prev) prev.disabled = rail.scrollLeft <= 4;
      if (next) next.disabled = rail.scrollLeft >= max - 4;
    };
    rail.addEventListener('scroll', updateRail, { passive: true });
    window.addEventListener('resize', updateRail);
    const step = (dir) => {
      smoothScroll(rail, { left: rail.scrollLeft + dir * rail.clientWidth * 0.85 });
      setTimeout(updateRail, 650);
    };
    if (prev) prev.addEventListener('click', () => step(-1));
    if (next) next.addEventListener('click', () => step(1));

    /* Toast */
    let toastTimer;
    const showToast = (title, cart) => {
      if (!toast) return;
      toast.querySelector('[data-pp-toast-title]').textContent = title;
      const ship = toast.querySelector('[data-pp-toast-ship]');
      if (cart) {
        const threshold = parseInt(ds.freeShip, 10) || 0;
        const line = fill(ds.labelCount, { count: cart.item_count, total: formatMoney(cart.total_price) });
        const left = threshold - cart.total_price;
        const shipLine = threshold ? (left > 0 ? fill(ds.labelShipLeft, { left: formatMoney(left) }) : ds.labelShipDone) : '';
        ship.textContent = shipLine ? `${line} · ${shipLine}` : line;
      } else {
        ship.textContent = '';
      }
      if (toast.hidden) {
        toast.hidden = false;
        enter(toast);
      }
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
    };

    const cartLink = toast && toast.querySelector('[data-pp-toast-cart]');
    if (cartLink) {
      cartLink.addEventListener('click', (event) => {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.show === 'function') {
          event.preventDefault();
          toast.hidden = true;
          drawer.show();
        }
      });
    }

    const flashAdded = (quick) => {
      if (!quick) return;
      const before = quick.dataset.ppAddedLabel ? quick.innerHTML : null;
      quick.classList.add('is-added');
      quick.innerHTML = quick.dataset.ppAddedLabel || TICK_ICON;
      setTimeout(() => {
        quick.classList.remove('is-added');
        quick.innerHTML = before || PLUS_ICON;
      }, 2200);
    };

    /* Size sheet */
    let sheetProduct = null;
    let sheetVariant = null;
    let sheetQuick = null;
    const sheetSizes = sheet && sheet.querySelector('[data-pp-sheet-sizes]');
    const sheetAdd = sheet && sheet.querySelector('[data-pp-sheet-add]');
    const sheetError = sheet && sheet.querySelector('[data-pp-sheet-error]');
    const sheetTitle = sheet && sheet.querySelector('[data-pp-sheet-title]');

    const setSheetButton = () => {
      sheetAdd.disabled = !sheetVariant;
      sheetAdd.textContent = sheetVariant ? `${ds.labelAdd} · ${sheetVariant.title}` : ds.labelPick;
    };

    const openSheet = (p, name, quick) => {
      sheetProduct = p;
      sheetVariant = null;
      sheetQuick = quick;
      const img = sheet.querySelector('[data-pp-sheet-img]');
      img.style.aspectRatio = String(p.ratio || 0.6667);
      if (p.image) {
        img.src = p.image;
        img.hidden = false;
      } else {
        img.hidden = true;
      }
      sheetTitle.textContent = name;
      sheet.querySelector('[data-pp-sheet-price]').textContent = p.price;
      sheet.querySelector('[data-pp-sheet-link]').href = p.url;
      sheetError.hidden = true;
      sheetSizes.innerHTML = '';
      p.variants.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pps-size';
        b.textContent = v.title;
        b.disabled = !v.available;
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', () => {
          sheetSizes.querySelectorAll('.pps-size').forEach((other) => other.setAttribute('aria-pressed', 'false'));
          b.setAttribute('aria-pressed', 'true');
          sheetVariant = v;
          setSheetButton();
        });
        sheetSizes.appendChild(b);
      });
      sheetSizes.style.setProperty('--pp-size-count', String(Math.min(p.variants.length, 7)));
      setSheetButton();
      sheet.showModal();
      enter(sheet);
    };

    if (sheet) {
      sheet.querySelector('[data-pp-sheet-close]').addEventListener('click', () => sheet.close());
      sheet.addEventListener('click', (event) => {
        if (event.target === sheet) sheet.close();
      });
      sheetAdd.addEventListener('click', async () => {
        if (!sheetVariant || sheetAdd.getAttribute('aria-busy') === 'true') return;
        sheetAdd.setAttribute('aria-busy', 'true');
        sheetError.hidden = true;
        try {
          const cart = await addToCart(sheetVariant.id, sheetProduct.id, { openDrawer: false });
          const title = `${ds.labelAdded}: ${sheetTitle.textContent} · ${sheetVariant.title}`;
          sheet.close();
          flashAdded(sheetQuick);
          showToast(title, cart);
        } catch (e) {
          sheetError.textContent = e.message;
          sheetError.hidden = false;
        } finally {
          sheetAdd.removeAttribute('aria-busy');
        }
      });
    }

    if (sheet && typeof sheet.showModal === 'function') {
      dropUi = { openSheet, showToast, flashAdded, labelAdded: ds.labelAdded };
    }

    loadPrint(ds.print)
      .then(({ products }) => {
        const list = products.filter((p) => String(p.id) !== ds.productId && p.available);
        if (!list.length) return;

        // Product names end in the noun ("Brief Bodysuit", "Pocket Yoga Pants"), so the group whose word appears
        // last in the title wins
        const groups = parseFilters(ds.filters);
        const groupOf = (title) => {
          const t = title.toLowerCase();
          let best = '';
          let bestAt = -1;
          groups.forEach((group) => {
            group.words.forEach((w) => {
              const at = t.lastIndexOf(w);
              if (at > bestAt) {
                bestAt = at;
                best = group.name;
              }
            });
          });
          return best;
        };

        // Single-size extras (scrunchies, headbands) after the sized styles, best sellers first within each
        list.sort((a, b) => (a.variants.length > 1 ? 0 : 1) - (b.variants.length > 1 ? 0 : 1));

        list.forEach((p) => {
          const name = shortTitle(p.title, ds.printName);
          const card = document.createElement('li');
          card.className = 'pps-card-p';
          card.dataset.group = groupOf(p.title);
          card.innerHTML = `
            <div class="pps-card-p__media" style="aspect-ratio: ${p.ratio || 0.6667}">
              <a class="pps-card-p__link" href="${p.url}" tabindex="-1" aria-hidden="true">
                ${p.image ? `<img src="${p.image}" alt="" loading="lazy" width="700" height="875">` : ''}
                ${p.image2 ? `<img src="${p.image2}" alt="" loading="lazy" width="700" height="875">` : ''}
              </a>
              <button type="button" class="pps-card-p__quick">${PLUS_ICON}</button>
            </div>
            <a class="pps-card-p__title" href="${p.url}"></a>
            <p class="pps-card-p__price"></p>`;
          card.querySelector('.pps-card-p__title').textContent = name;
          card.querySelector('.pps-card-p__price').textContent = p.price;

          const quick = card.querySelector('.pps-card-p__quick');
          quick.setAttribute('aria-label', fill(ds.labelQuick, { name }));
          const single = p.variants.length === 1 ? p.variants[0] : null;

          quick.addEventListener('click', async () => {
            if (!single) {
              if (sheet && typeof sheet.showModal === 'function') openSheet(p, name, quick);
              else window.location.href = p.url;
              return;
            }
            if (quick.getAttribute('aria-busy') === 'true') return;
            quick.setAttribute('aria-busy', 'true');
            try {
              const cart = await addToCart(single.id, p.id, { openDrawer: false });
              flashAdded(quick);
              showToast(`${ds.labelAdded}: ${name}`, cart);
            } catch (e) {
              showToast(e.message, null);
            } finally {
              quick.removeAttribute('aria-busy');
            }
          });

          track.appendChild(card);
        });

        // Filter chips: only groups that have something in them
        const used = groups.filter((g) => list.some((p) => groupOf(p.title) === g.name));
        if (filtersEl && used.length > 1) {
          const chips = [{ name: ds.labelAll || 'All' }, ...used].map((g, i) => {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'pps-chip';
            chip.setAttribute('role', 'tab');
            chip.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
            chip.textContent = g.name;
            chip.addEventListener('click', () => {
              chips.forEach((c) => c.setAttribute('aria-selected', 'false'));
              chip.setAttribute('aria-selected', 'true');
              track.querySelectorAll('.pps-card-p').forEach((card) => {
                card.hidden = i !== 0 && card.dataset.group !== g.name;
              });
              rail.scrollLeft = 0;
              updateRail();
            });
            filtersEl.appendChild(chip);
            return chip;
          });
        } else if (filtersEl) {
          filtersEl.remove();
        }

        section.hidden = false;
        updateRail();
      })
      .catch(() => {});
  }

  /* ---------- Complete the look (buy box) ---------- */

  // The pieces this product is photographed with. Sized pieces open Shop the drop's size sheet; one-size pieces
  // add in one tap. Without the drop section on the page, sized pieces link to their page instead.
  function initLook(look) {
    const list = look.querySelector('[data-pp-look-list]');
    const json = look.querySelector('[data-pp-look-products]');
    const error = look.querySelector('[data-pp-addon-error]');
    if (!list || !json) return;

    let products = [];
    try {
      products = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }
    if (!products.length) return;

    products.forEach((p) => {
      const name = shortTitle(p.title, look.dataset.printName);
      const row = document.createElement('li');
      row.className = 'ppm-addon__row';
      row.innerHTML = `
        ${p.image ? `<img class="ppm-addon__img" src="${p.image}" alt="" loading="lazy" style="aspect-ratio: ${p.ratio || 0.6667}">` : '<span></span>'}
        <a class="ppm-addon__info" href="${p.url}">
          <span class="ppm-addon__title"></span>
          <span class="ppm-addon__price"></span>
        </a>
        <button type="button" class="ppm-addon__btn"></button>`;
      row.querySelector('.ppm-addon__title').textContent = name;
      row.querySelector('.ppm-addon__price').textContent = p.price;

      const button = row.querySelector('.ppm-addon__btn');
      button.textContent = look.dataset.addText || 'Add';
      button.dataset.ppAddedLabel = look.dataset.addedText || 'Added';
      button.setAttribute('aria-label', `${button.textContent} ${name}`);
      const single = p.variants.length === 1 ? p.variants[0] : null;

      button.addEventListener('click', async () => {
        if (!single) {
          if (dropUi) dropUi.openSheet(p, name, button);
          else window.location.href = p.url;
          return;
        }
        if (button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');
        error.hidden = true;
        try {
          const cart = await addToCart(single.id, p.id, { openDrawer: !dropUi });
          if (dropUi) {
            dropUi.flashAdded(button);
            dropUi.showToast(`${dropUi.labelAdded}: ${name}`, cart);
          }
        } catch (e) {
          error.textContent = e.message;
          error.hidden = false;
        } finally {
          button.removeAttribute('aria-busy');
        }
      });

      list.appendChild(row);
    });

    look.hidden = false;
  }

  /* ---------- Mobile pre-order bar ---------- */

  function initBar(bar) {
    const cta = document.querySelector('[data-pp-cta]');
    const sizes = document.querySelector('[data-pp-sizes]');
    const button = bar.querySelector('[data-pp-bar-btn]');
    const sizeText = bar.querySelector('[data-pp-bar-size]');
    const form = document.forms[bar.dataset.form];
    if (!cta || !button) return;

    // One-size pieces are ready to go. Multi-size ones wait for a real pick: never read the checked radio on
    // load, the size gate (assets/require-size.js) clears the server default a moment later.
    let picked = bar.hasAttribute('data-single');
    let ctaVisible = true;
    let footerVisible = false;

    const mobile = window.matchMedia('(max-width: 999px)');
    const update = () => {
      bar.hidden = ctaVisible || footerVisible;
      document.documentElement.classList.toggle('pp-bar-on', !bar.hidden && mobile.matches);
    };
    const setLabel = () => {
      button.textContent = picked ? button.dataset.buy : button.dataset.choose;
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target.matches('[data-pp-cta]')) ctaVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
        else footerVisible = entry.isIntersecting;
      });
      update();
    });
    observer.observe(cta);
    const footer = document.querySelector('.shopify-section--footer');
    if (footer) observer.observe(footer);

    // The size picker is re-rendered by Prestige on every change, so listen on the document
    document.addEventListener('change', (event) => {
      if (event.target.matches && event.target.matches('[data-pp-sizes] input[data-option-position]')) {
        picked = true;
        setLabel();
        const label = event.target.id && document.querySelector(`label[for="${CSS.escape(event.target.id)}"]`);
        sizeText.textContent = (label && label.textContent.trim()) || event.target.value;
      }
    });

    button.addEventListener('click', () => {
      if (picked && form && typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else if (sizes) {
        scrollToEl(sizes);
      }
    });

    setLabel();
  }

  /* ---------- Fit and fabric: "Show all" on the details list ---------- */

  document.addEventListener('click', (event) => {
    const more = event.target.closest && event.target.closest('[data-pp-ticks-more]');
    if (!more) return;
    const list = more.parentElement.querySelector('[data-pp-ticks]');
    if (list) list.classList.remove('is-clamped');
    more.remove();
  });

  /* ---------- How it's made: video only loads when it's about to be seen ---------- */

  function initVideo(frame) {
    const video = frame.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;

    const saveData = navigator.connection && navigator.connection.saveData;
    if (reduceMotion || saveData || !('IntersectionObserver' in window)) {
      video.controls = true; // their choice to press play
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {
              video.controls = true; // autoplay blocked: show the play button instead
            });
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(frame);
  }

  /* ---------- Fit and fabric: model toggle (B / Tahisha) ---------- */

  document.addEventListener('click', (event) => {
    const tab = event.target.closest && event.target.closest('[data-pp-fit-tab]');
    if (!tab) return;
    const card = tab.closest('[data-pp-fit]');
    const index = parseInt(tab.dataset.ppFitTab, 10);
    card.querySelectorAll('[data-pp-fit-tab]').forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
    card.querySelectorAll('[data-pp-fit-panel]').forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  });

  /* ---------- Jump links ---------- */

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('[data-pp-jump]');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target || target.closest('[hidden]')) return;
    event.preventDefault();
    scrollToEl(target);
  });

  document.querySelectorAll('[data-pp-gallery]').forEach(initGallery);
  document.querySelectorAll('[data-pp-meter]').forEach(initMeter);
  document.querySelectorAll('[data-pp-addon]').forEach(initAddon);
  document.querySelectorAll('[data-pp-drop]').forEach(initDrop);
  document.querySelectorAll('[data-pp-look]').forEach(initLook);
  document.querySelectorAll('[data-pp-bar]').forEach(initBar);
  document.querySelectorAll('[data-pp-video]').forEach(initVideo);
})();
